import React, { type PropsWithChildren, useMemo, useState } from 'react';

import { analyticsProps } from '@class-ui/insights';
import Icon from '@mdi/react';
import { MoreHoriz } from '@mui/icons-material';
import {
    Box,
    Button,
    type ButtonPropsColorOverrides,
    Divider,
    IconButton,
    LinearProgress,
    ListItemIcon,
    Menu,
    MenuItem,
    styled,
    Typography
} from '@mui/material';
import { type OverridableStringUnion } from '@mui/types';

import ClassLogoForHeader from '../../icons/ClassLogoForHeader';

const Container = styled(Box)`
    display: flex;
    flex-direction: column;
` as typeof Box;

const CenteringWrapper = styled(Box)`
    display: flex;
    justify-content: center;
` as typeof Box;

const Content = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'narrowStyle'
})<{ narrowStyle?: boolean }>`
    padding: 28px ${({ narrowStyle }) => (narrowStyle ? '16px' : '40px')};
    max-width: 1500px;
    width: 100%;
    margin: 0 auto;
`;

const LinearProgressSpacer = styled(Box)`
    height: 4px;
` as typeof Box;

const Header = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'narrowStyle'
})<{ narrowStyle?: boolean }>`
    display: flex;
    align-items: center;
    color: white;
    background-color: ${(props) => props.theme.palette.primary.main};
    height: 56px;
    padding: 0px ${({ narrowStyle }) => (narrowStyle ? '20px' : '44px')};
`;

const SubHeader = styled(Box)`
    display: flex;
    align-items: center;
` as typeof Box;

const HeaderImg = styled('img')`
    max-height: 40px;
    margin-right: 4px;
`;

const HeaderIcon = styled(Box)`
    flex: 0 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
        0px 3px 5px -1px rgb(0 0 0 / 20%),
        0px 6px 10px 0px rgb(0 0 0 / 14%),
        0px 1px 18px 0px rgb(0 0 0 / 12%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 4px;
    background-color: ${(props) => props.theme.palette.common.white};

    svg {
        color: ${(props) => props.theme.palette.primary.main};
        width: 25px;
        margin-bottom: 1px;
    }
` as typeof Box;

const SubHeaderIcon = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-left: 12px;
` as typeof Box;

const HeaderTitle = styled(Typography)`
    display: inline-block;
    line-height: 32px;
    white-space: nowrap;
    font-weight: 400;
    &:hover {
        text-decoration: none;
    }
` as typeof Typography;

const HeaderTitleButton = styled(Button)`
    color: ${(props) => props.theme.palette.common.white};
    display: inline-block;
    line-height: 32px;
    white-space: nowrap;
    font-weight: 400;
    padding: 0;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 5px;
` as typeof Button;

const HeaderControls = styled(Box)`
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
` as typeof Box;

const StyledButton = styled(Button)`
    white-space: nowrap;
    font-size: 14px;
    margin-left: 6px;
    min-width: 42px;

    /* Remove spacing between button text and icon */
    & .MuiButton-endIcon {
        margin-left: 2px;
        margin-right: 2px;
    }

    & .MuiButton-startIcon {
        margin-right: 4px;
    }

    &.Mui-disabled {
        border-color: ${(props) => props.theme.palette.common.white};
        color: ${(props) => props.theme.palette.common.white};
        opacity: 0.3;
    }
`;

const StyledMenuButton = styled(Button)`
    padding: 0px 7px;
    white-space: nowrap;
    font-size: 14px;
    margin-left: 6px;
    min-width: 42px;
` as typeof Button;

const StyledMenuIcon = styled(ListItemIcon)`
    font-size: 14px;
    min-width: 30px;
` as typeof ListItemIcon;

const StyledMenuText = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
` as typeof Typography;

const StyledIconButton = styled(IconButton)`
    font-size: 18px;
    margin-left: 6px;
    min-width: 42px;
`;

export type NavControlItem = React.ReactNode | NavControl | NavControlMenu;

/**
 * Control rendered as a button in the layout header.
 */
export interface NavControl {
    label: string;
    onClick: () => void;
    section?: number;
    icon?: React.ReactNode;
    disabled?: boolean;
    dataTestId?: string;
    color?: OverridableStringUnion<
        'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
        ButtonPropsColorOverrides
    >;
    ariaLabel?: string;
}

/**
 * Control rendered as a button with dropdown menu in the layout header.
 */
export interface NavControlMenu {
    label: string;
    disabled?: boolean;
    dataTestId?: string;
    menu: NavControl[];
}

export interface SubTitleItem {
    title: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    iconSize?: string;
    sx?: React.CSSProperties;
}

/**
 * Main layout component.
 */
export interface MainLayoutProps {
    title: string;
    titleOnClick?: () => void;
    subTitles?: SubTitleItem[];
    icon?: string;
    img?: string;
    controls?: NavControlItem[];
    loading?: boolean;
    narrowStyle?: boolean;
}

interface NavControlComponentProps {
    control: NavControl;
}

interface NavControlMenuComponentProps {
    control: NavControlMenu;
    id: number;
}

const headerSx = {
    fontSize: '22px',
    marginLeft: '6px'
};

/**
 * Groups controls by section. In the order in which they are passed.
 * @param navControls - controls to group
 */
function groupNavControlsBySection(navControls: NavControl[]): { [key: number]: NavControl[] } {
    const groupedNavControls: { [key: number]: NavControl[] } = {};

    navControls.forEach((navControl) => {
        const section = navControl.section ?? 0;

        if (!groupedNavControls[section]) {
            groupedNavControls[section] = [];
        }
        groupedNavControls[section].push(navControl);
    });
    return groupedNavControls;
}

/**
 * Renders dropdown menu with controls.
 * @param menuItems - controls to render
 * @param handleMenuSelect - callback for menu item selection
 */
function renderMenuNavControls(menuItems: NavControl[], handleMenuSelect: (NavControl) => void): React.ReactNode[] {
    const groupedNavControls = groupNavControlsBySection(menuItems);
    const sections = Object.keys(groupedNavControls);

    return sections.flatMap((section, sectionIndex) => [
        sectionIndex > 0 && <Divider key={`${section}-divider`} />,
        ...groupedNavControls[section].map((menuItem, index) => (
            <MenuItem
                key={`${section}-${index}`}
                onClick={() => handleMenuSelect(menuItem)}
                disableRipple
                disabled={menuItem.disabled === undefined ? false : menuItem.disabled}
                data-testid={menuItem.dataTestId}
            >
                {menuItem.icon && <StyledMenuIcon>{menuItem.icon}</StyledMenuIcon>}
                <StyledMenuText color={menuItem.color ?? 'main'}>{menuItem.label}</StyledMenuText>
            </MenuItem>
        ))
    ]);
}

/**
 * Single control as a button.
 * @param {NavControl} control - control to render
 */
const NavControlComponent = (props: NavControlComponentProps) => {
    const { control } = props;

    if (control.label.length) {
        return (
            <StyledButton
                variant="outlined"
                color={control?.color ?? 'inherit'}
                size="small"
                onClick={control.onClick}
                disabled={control.disabled === undefined ? false : control.disabled}
                data-testid={control.dataTestId}
                startIcon={control.icon}
                aria-label={control.ariaLabel}
            >
                {control.label}
            </StyledButton>
        );
    }

    if (control.icon) {
        return (
            <StyledIconButton
                color={control?.color ?? 'inherit'}
                size="small"
                onClick={control.onClick}
                disabled={control.disabled === undefined ? false : control.disabled}
                data-testid={control.dataTestId}
                aria-label={control.ariaLabel}
            >
                {control.icon}
            </StyledIconButton>
        );
    }

    throw Error('Control must have at least a label or an icon defined.');
};

/**
 * Button with dropdown menu. The menu is rendered as a Material UI Menu component.
 * The section property of each NavControl in the passed {@code control.menu} is used to group menu items, separated by a divider.
 * The sections do not have to be ordered, the tool will sort them by section number.
 * @param {NavControlMenu} control - control to render
 * @param {number} id Uniquely identify dropdown menu.
 */
const NavControlMenuComponent = (props: NavControlMenuComponentProps) => {
    const { control, id } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuSelect = (menuItem: NavControl) => {
        setAnchorEl(null);
        menuItem.onClick();
    };

    return (
        <>
            <StyledMenuButton
                variant="outlined"
                color="inherit"
                size="small"
                disabled={control.disabled === undefined ? false : control.disabled}
                id={`nav-menu-button-${id}`}
                aria-controls={open ? `nav-menu-${id}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                data-testid={control.dataTestId}
                disableElevation
                onClick={handleClick}
            >
                <MoreHoriz fontSize="large" />
                {control.label}
            </StyledMenuButton>
            <Menu
                id={`nav-menu-${id}`}
                MenuListProps={{ 'aria-labelledby': `nav-menu-button-${id}` }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {renderMenuNavControls(control.menu, handleMenuSelect)}
            </Menu>
        </>
    );
};

/**
 * Wrap page content
 */
const MainLayout = (props: PropsWithChildren<MainLayoutProps>) => {
    const { icon, img, title, titleOnClick, subTitles, controls, loading, narrowStyle, children } = props;
    const renderedControls = useMemo(() => {
        if (!controls) {
            return;
        }

        return controls.map((control, index) => {
            if (React.isValidElement(control)) {
                return control;
            }
            if ((control as any).menu) {
                return <NavControlMenuComponent key={index} id={index} control={control as NavControlMenu} />;
            }

            return <NavControlComponent key={index} control={control as NavControl} />;
        });
    }, [controls]);

    return (
        <Container>
            <Header narrowStyle={narrowStyle}>
                {icon && (
                    <HeaderIcon>
                        <Icon path={icon} />
                    </HeaderIcon>
                )}
                {img && <HeaderImg src={img} alt="logo" />}
                {!icon && !img && <ClassLogoForHeader />}
                {titleOnClick ? (
                    <HeaderTitleButton
                        variant="text"
                        onClick={titleOnClick}
                        sx={headerSx}
                        {...analyticsProps('main-layout-title')}
                    >
                        {title}
                    </HeaderTitleButton>
                ) : (
                    <HeaderTitle variant="h1" sx={headerSx} onClick={titleOnClick}>
                        {title}
                    </HeaderTitle>
                )}
                {subTitles?.map(({ title: subTitle, onClick: subTitleOnClick, icon, iconSize, sx }, index) => (
                    <SubHeader key={`header-title-box-${index}`}>
                        {icon && <SubHeaderIcon>{icon}</SubHeaderIcon>}
                        {subTitleOnClick ? (
                            <HeaderTitleButton
                                variant="text"
                                onClick={subTitleOnClick}
                                key={`header-title-button-${index}`}
                                sx={{ ...headerSx, marginLeft: icon ? '2px' : '6px', ...sx }}
                            >
                                {icon ? subTitle : '/ ' + subTitle}
                            </HeaderTitleButton>
                        ) : (
                            <HeaderTitle
                                key={`header-title-${index}`}
                                variant="h1"
                                sx={{ ...headerSx, marginLeft: icon ? '2px' : '6px', ...sx }}
                                onClick={subTitleOnClick}
                            >
                                {icon ? subTitle : '/ ' + subTitle}
                            </HeaderTitle>
                        )}
                    </SubHeader>
                ))}
                <HeaderControls>{renderedControls}</HeaderControls>
            </Header>

            {loading ? (
                <LinearProgress aria-busy="true" aria-live="polite" {...analyticsProps('main-layout-loading')} />
            ) : (
                <LinearProgressSpacer />
            )}

            <CenteringWrapper>
                <Content narrowStyle={narrowStyle}>{children}</Content>
            </CenteringWrapper>
        </Container>
    );
};

export default MainLayout;
